import React from 'react';
import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { DateObject, formatTimeRangeWithMeridiem } from '@uplift/services';
import { Warning } from '@mui/icons-material';
import { EventItemVariant } from '../../EventItem';


const useStyles = makeStyles()(theme => ({
  root: {
    padding: theme.spacing(1, 2),
    boxSizing: 'border-box',
    height: '64px',
    display: 'flex',
    alignItems: 'center',
    userSelect: 'none',
  },
  rootDense: {
    height: '48px',
  },
  clickable: {
    cursor: 'pointer',
  },
  warning: {
    color: theme.palette.warning.dark,
  },
  warningIcon: {
    color: theme.palette.warning.main,
  },
  indicatorColorRegular: {
    backgroundColor: theme.palette.secondary.main,
  },
  indicatorColorWarning: {
    backgroundColor: theme.palette.warning.main,
  },
  indicatorColorICS: {
    backgroundColor: theme.palette.info.background,
  },
  indicatorColorWarningConflict: {
    backgroundImage: `repeating-linear-gradient(135deg,
      ${theme.palette.warning.main},
      ${theme.palette.warning.main} 4px,
      ${theme.palette.warning.dark} 4px,
      ${theme.palette.warning.dark} 10px
    )`,
  },
}));


export type MobileCalendarEventItemProps = {
  title: string;
  start: DateObject;
  end: DateObject;
  variant: EventItemVariant;
  onClick?: () => void;
  allDay?: boolean;
};


export const MobileCalendarEventItem: React.FC<MobileCalendarEventItemProps> = ({ title, start, end, onClick, variant, allDay }) => {
  const { classes, cx } = useStyles();
  const time = React.useMemo(() => formatTimeRangeWithMeridiem(start, end, { forceMinutes: false, separator: '–' }), [ start, end ]);

  const isWarningIndicator = variant === 'appointmentOverlapWarning';
  const indicatorColorClassName = {
    appointment: classes.indicatorColorRegular,
    appointmentOverlapWarning: classes.indicatorColorWarningConflict,
    appointmentWarning: classes.indicatorColorWarning,
    availability: classes.indicatorColorRegular,
    availabilityWarning: classes.indicatorColorWarningConflict,
    ics: classes.indicatorColorICS,
  }[variant];

  return (
    <Box
      onClick={onClick}
      className={cx(classes.root, {
        [classes.rootDense]: allDay,
        [classes.clickable]: !!onClick,
      })}
    >
      <Box width="6px" height="100%" borderRadius="3px" className={indicatorColorClassName} />
      <Box ml={1} flex="1">
        <Typography variant="body1" className={cx({ [classes.warning]: isWarningIndicator })} style={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>
      {isWarningIndicator && (
        <Box mr={1} display="flex">
          <Warning className={classes.warningIcon} />
        </Box>
      )}
      <Box>
        <Typography variant="body1" color="textPrimary">
          {allDay ? 'all day' : time}
        </Typography>
      </Box>
    </Box>
  );
};
